import { useModal } from '@tager/web-components';

import { useTypedSelector } from '@/store/store';
import { selectDealers } from '@/store/reducers/dealers';
import { selectDetailedCarData } from '@/store/reducers/detailed-car';
import { selectSeoTemplate } from '@/store/reducers/seo-template';
import useSettingItem from '@/hooks/useSettingItem';
import {
  getDetailedCarBreadcrumbMicromarking,
  getDetailedCarProductMicromarking,
  getEventMicromarking,
} from '@/utils/seo';
import { usePageUrl } from '@/hooks/usePageUrl';
import { CarsType } from '@/typings/common';
import { getFirstDateOfYear, getLastDateOfYear } from '@/utils/date';
import { getAverageRatingAndCountReviewsDealers } from '@/modules/Dealers/helpers';
import CreditModal from '@/components/modals/CreditModal';
import defaultStockCar from '@/assets/images/cars-stock/defaultStockCar.png';
import { StyledOverlay } from '@/components/modals/shared/StyledOverlay';
import { CarsStockFuel } from '@/services/stock/enums';

import {
  convertDetailedCarSEOTemplate,
  getCarViewBody,
  getDealersOptions,
  getEventParams,
} from './DetailedCar.helper';

export const useDetailedCarSeo = (carType: CarsType) => {
  const dealers = useTypedSelector(selectDealers);
  const detailedCarData = useTypedSelector(selectDetailedCarData);
  const seoTemplate = useTypedSelector(selectSeoTemplate);
  const organization = useSettingItem('ORGANIZATION_NAME');
  const address = useSettingItem('MAIN_ADDRESS');
  const url = usePageUrl();

  const { rating, ratingCount } =
    getAverageRatingAndCountReviewsDealers(dealers);
  const isRatingOff =
    detailedCarData?.params.fuel?.id === CarsStockFuel.Electric ||
    detailedCarData?.params.fuel?.id === CarsStockFuel.Hybrid;

  const convertedSeoTemplate = convertDetailedCarSEOTemplate(
    detailedCarData,
    seoTemplate
  );

  const seoParams = {
    h1: convertedSeoTemplate?.h1 ?? '',
    title: convertedSeoTemplate?.title ?? '',
    description: convertedSeoTemplate?.description ?? '',
    openGraphImage: convertedSeoTemplate?.openGraphImage ?? '',
    keywords: convertedSeoTemplate?.keywords ?? '',
  };

  const productMicromarkingData = {
    h1: `${detailedCarData?.catalog?.brand?.name} ${detailedCarData?.catalog?.model?.name}`,
    image: seoParams.openGraphImage,
    description: seoParams.description,
    vin: detailedCarData?.vin ?? '',
    brand: detailedCarData?.catalog?.brand?.name ?? '',
    rating,
    ratingCount,
    url,
    price:
      (detailedCarData?.pricing?.discountPrice
        ? detailedCarData?.pricing?.discountPrice
        : detailedCarData?.pricing?.price) ?? '',
    organization: organization ?? '',
  };
  const breadcrumbMicromarkingData = {
    brand: detailedCarData?.catalog?.brand?.name ?? '',
    model: detailedCarData?.catalog?.model?.name ?? '',
    brandAlias: detailedCarData?.catalog?.brand?.slug ?? '',
    modelAlias: detailedCarData?.catalog?.model?.slug ?? '',
  };

  const eventMicromarkingNames = [
    '✔️ Большой выбор',
    '🔥 Онлайн-покупка',
    '🔥 Тест-драйв',
  ];
  const eventMicromarkingData = eventMicromarkingNames.map((name) => ({
    image: seoParams.openGraphImage,
    name,
    description: seoParams.description,
    startDate: getFirstDateOfYear(),
    endDate: getLastDateOfYear(),
    url,
    organization: organization ?? '',
    address: address ?? '',
  }));

  return {
    ...seoParams,
    productMicromarking: getDetailedCarProductMicromarking(
      productMicromarkingData,
      {
        ratingOff: isRatingOff,
      }
    ),
    breadcrumbMicromarking: getDetailedCarBreadcrumbMicromarking(
      carType,
      breadcrumbMicromarkingData
    ),
    eventMicromarking: eventMicromarkingData.map((data) =>
      getEventMicromarking(data)
    ),
  };
};

export const useCreditModal = () => {
  const data = useTypedSelector(selectDetailedCarData);
  const openModal = useModal();

  return () => {
    if (!data) {
      return;
    }

    const {
      type,
      id,
      media: { images },
      catalog: { brand, model },
      params: { body },
      pricing,
    } = data;

    openModal(
      CreditModal,
      {
        creditType: 'credit',
        carType: type,
        carId: id,
        image: {
          url: images.length ? images[0] : defaultStockCar,
        },
        name: `${brand.name} ${model?.name}`,
        carViewBody: body?.id ? getCarViewBody(body.id) : 'crossover',
        price:
          (pricing?.discountPrice ? pricing.discountPrice : pricing?.price) ??
          0,
        body: body?.label ?? '',
        dealers: getDealersOptions(data),
        googleEventParams: getEventParams(data),
      },
      {
        components: { Overlay: StyledOverlay },
        scrollLockDisabled: true,
        shouldCloseOnClickOutside: false,
        shouldCloseOnEscape: false,
      }
    );
  };
};
